import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { Row, Col, Table, Card, Tag, Progress, Statistic } from 'antd';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip,
  ResponsiveContainer, PieChart, Pie, Cell, Legend,
} from 'recharts';
import { useLazyQuery } from '@apollo/client';
import dayjs from 'dayjs';
import { GET_EMPLOYEE_TIMER_STATS } from '../../../Queries/EmployeeTimerStats';
import { AntdButton, DatePickerField, SelectInput } from '../../../blueplanitUtils/InputField';

const COLORS = ['#3498db', '#55ce63', '#ff9b44', '#fc6075', '#667eea', '#17a6df', '#FF5733', '#35BA67'];
const SOURCE_COLORS = { 'auto-detected': '#3498db', 'manual-inline': '#55ce63', manual: '#ff9b44' };

const formatHours = (hours) => {
  if (!hours) return '0h 0m';
  const h = Math.floor(hours);
  const m = Math.round((hours - h) * 60);
  return `${h}h ${m}m`;
};

const cardStyle = {
  borderRadius: '10px',
  border: 'none',
  boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
};

const kpiValueStyle = (color) => ({
  fontSize: '28px',
  fontWeight: 700,
  color: color || '#1a1a2e',
  lineHeight: 1.2,
});

const kpiLabelStyle = {
  fontSize: '12px',
  color: '#8c8c8c',
  fontWeight: 500,
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  marginBottom: '4px',
};

const kpiIconStyle = (bg) => ({
  width: '48px',
  height: '48px',
  borderRadius: '12px',
  background: bg,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '22px',
});

const sectionTitleStyle = {
  fontSize: '14px',
  fontWeight: 600,
  color: '#1a1a2e',
  marginBottom: '12px',
};

const EmployeePerformanceReport = () => {
  const loginUser = useSelector((state) => state?.loginUserInfo?.loginUserDetails);
  const [startDate, setStartDate] = useState(dayjs().startOf('month'));
  const [endDate, setEndDate] = useState(dayjs());
  const [department, setDepartment] = useState('');
  const [expandedRows, setExpandedRows] = useState([]);

  const [fetchStats, { data, loading }] = useLazyQuery(GET_EMPLOYEE_TIMER_STATS, {
    fetchPolicy: 'no-cache',
  });

  const handleSearch = () => {
    fetchStats({
      variables: {
        startDate: startDate.valueOf().toString(),
        endDate: endDate.valueOf().toString(),
        department: department || undefined,
        loginUserId: loginUser?.id,
      },
    });
  };

  const stats = data?.getEmployeeTimerStats;
  const employees = stats?.employees || [];

  // Compute KPIs
  const totalHours = employees.reduce((s, e) => s + (e.totalActiveHours || 0), 0);
  const totalSessions = employees.reduce((s, e) => s + (e.totalSessions || 0), 0);
  const avgAutoRate = employees.length
    ? Math.round(employees.reduce((s, e) => s + (e.autoDetectionRate || 0), 0) / employees.length)
    : 0;

  // Chart: top 10 employees by hours
  const topEmployees = [...employees]
    .sort((a, b) => b.totalActiveHours - a.totalActiveHours)
    .slice(0, 10)
    .map((e) => ({
      name: e.employeeName?.split(' ')[0] || e.userId?.slice(0, 8),
      hours: e.totalActiveHours || 0,
      sessions: e.totalSessions || 0,
    }));

  // Chart: aggregated source breakdown
  const sourceAgg = {};
  employees.forEach((e) => {
    (e.sourceBreakdown || []).forEach((s) => {
      const key = s.source || 'manual';
      sourceAgg[key] = (sourceAgg[key] || 0) + (s.totalHours || 0);
    });
  });
  const sourceData = Object.entries(sourceAgg).map(([name, value]) => ({
    name: name === 'auto-detected' ? 'Auto-Detected' : name === 'manual-inline' ? 'Manual Inline' : 'Manual',
    value: Math.round(value * 100) / 100,
    key: name,
  }));

  const columns = [
    {
      title: 'Employee',
      dataIndex: 'employeeName',
      key: 'employeeName',
      width: 200,
      render: (val, record) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <div style={{
            width: '32px', height: '32px', borderRadius: '50%',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: '#fff', fontSize: '13px', fontWeight: 600,
          }}>
            {(val || '?')[0]?.toUpperCase()}
          </div>
          <div>
            <div style={{ fontWeight: 600, fontSize: '13px', color: '#1a1a2e' }}>{val}</div>
            <div style={{ fontSize: '11px', color: '#8c8c8c' }}>{record.department || '-'}</div>
          </div>
        </div>
      ),
    },
    {
      title: 'Active Hours',
      dataIndex: 'totalActiveHours',
      key: 'totalActiveHours',
      width: 130,
      render: (val) => (
        <span style={{ fontWeight: 600, fontSize: '13px', color: '#1a1a2e' }}>{formatHours(val)}</span>
      ),
      sorter: (a, b) => a.totalActiveSeconds - b.totalActiveSeconds,
      defaultSortOrder: 'descend',
    },
    {
      title: 'Sessions',
      dataIndex: 'totalSessions',
      key: 'totalSessions',
      width: 90,
      render: (val) => <span style={{ fontWeight: 500 }}>{val}</span>,
    },
    {
      title: 'Requests',
      dataIndex: 'uniqueRequests',
      key: 'uniqueRequests',
      width: 90,
      render: (val) => <span style={{ fontWeight: 500 }}>{val}</span>,
    },
    {
      title: 'Auto-Detection Rate',
      dataIndex: 'autoDetectionRate',
      key: 'autoDetectionRate',
      width: 200,
      render: (val) => {
        const color = val >= 70 ? '#55ce63' : val >= 40 ? '#faad14' : '#ff4d4f';
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <Progress
              percent={val || 0}
              size="small"
              style={{ width: '100px', margin: 0 }}
              strokeColor={color}
              trailColor="#f0f0f0"
              showInfo={false}
            />
            <Tag
              color={val >= 70 ? 'success' : val >= 40 ? 'warning' : 'error'}
              style={{ borderRadius: '12px', fontWeight: 600, fontSize: '12px', minWidth: '45px', textAlign: 'center' }}
            >
              {val || 0}%
            </Tag>
          </div>
        );
      },
      sorter: (a, b) => (a.autoDetectionRate || 0) - (b.autoDetectionRate || 0),
    },
  ];

  const taskColumns = [
    {
      title: 'Task',
      dataIndex: 'taskType',
      key: 'taskType',
      width: 200,
      render: (val) => <span style={{ fontWeight: 500 }}>{val}</span>,
    },
    {
      title: 'Hours',
      dataIndex: 'totalHours',
      key: 'totalHours',
      width: 120,
      render: (v) => <span style={{ fontWeight: 600, color: '#3498db' }}>{formatHours(v)}</span>,
    },
    {
      title: 'Sessions',
      dataIndex: 'sessionCount',
      key: 'sessionCount',
      width: 80,
    },
  ];

  const sourceColumns = [
    {
      title: 'Source',
      dataIndex: 'source',
      key: 'source',
      width: 160,
      render: (val) => {
        const label = val === 'auto-detected' ? 'Auto-Detected' : val === 'manual-inline' ? 'Manual Inline' : 'Manual';
        const color = val === 'auto-detected' ? 'blue' : val === 'manual-inline' ? 'green' : 'default';
        return <Tag color={color} style={{ borderRadius: '12px', fontWeight: 500 }}>{label}</Tag>;
      },
    },
    {
      title: 'Hours',
      dataIndex: 'totalHours',
      key: 'totalHours',
      width: 120,
      render: (v) => <span style={{ fontWeight: 600, color: '#55ce63' }}>{formatHours(v)}</span>,
    },
    {
      title: 'Sessions',
      dataIndex: 'sessionCount',
      key: 'sessionCount',
      width: 80,
    },
  ];

  const CustomPieLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }) => {
    const RADIAN = Math.PI / 180;
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);
    if (percent < 0.05) return null;
    return (
      <text x={x} y={y} fill="#fff" textAnchor="middle" dominantBaseline="central" fontSize={12} fontWeight={600}>
        {`${(percent * 100).toFixed(0)}%`}
      </text>
    );
  };

  return (
    <div style={{ padding: '24px', background: '#f5f6fa', minHeight: '100vh' }}>
      {/* Header */}
      <div style={{ marginBottom: '20px' }}>
        <h4 style={{ fontWeight: 700, fontSize: '20px', color: '#1a1a2e', margin: 0 }}>
          Employee Performance Dashboard
        </h4>
        <p style={{ color: '#8c8c8c', fontSize: '13px', margin: '4px 0 0' }}>
          Monitor employee work hours, session activity, and automation metrics
        </p>
      </div>

      {/* Filters */}
      <Card size="small" style={{ ...cardStyle, marginBottom: '20px' }}>
        <Row gutter={16} align="bottom">
          <Col span={6}>
            <DatePickerField label="Start Date" value={startDate} onChange={setStartDate} format="DD/MM/YYYY" />
          </Col>
          <Col span={6}>
            <DatePickerField label="End Date" value={endDate} onChange={setEndDate} format="DD/MM/YYYY" />
          </Col>
          <Col span={5}>
            <SelectInput
              label="Department"
              value={department}
              onChange={setDepartment}
              options={[
                { value: '', label: 'All Departments' },
                { value: 'Operations', label: 'Operations' },
                { value: 'Sales', label: 'Sales' },
              ]}
            />
          </Col>
          <Col span={4}>
            <AntdButton
              label={loading ? 'Loading...' : 'Generate Report'}
              color="primary"
              variant="solid"
              onClick={handleSearch}
              disabled={loading}
              style={{ marginTop: '22px', borderRadius: '8px', fontWeight: 600 }}
            />
          </Col>
        </Row>
      </Card>

      {/* KPI Cards */}
      <Row gutter={16} style={{ marginBottom: '20px' }}>
        <Col span={6}>
          <Card size="small" style={cardStyle} bodyStyle={{ padding: '20px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={kpiLabelStyle}>Total Employees</div>
                <div style={kpiValueStyle('#1a1a2e')}>{stats?.totalCount || 0}</div>
              </div>
              <div style={kpiIconStyle('linear-gradient(135deg, #667eea 0%, #764ba2 100%)')}>👥</div>
            </div>
          </Card>
        </Col>
        <Col span={6}>
          <Card size="small" style={cardStyle} bodyStyle={{ padding: '20px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={kpiLabelStyle}>Total Active Hours</div>
                <div style={kpiValueStyle('#3498db')}>{formatHours(totalHours)}</div>
              </div>
              <div style={kpiIconStyle('linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)')}>⏱</div>
            </div>
          </Card>
        </Col>
        <Col span={6}>
          <Card size="small" style={cardStyle} bodyStyle={{ padding: '20px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={kpiLabelStyle}>Total Sessions</div>
                <div style={kpiValueStyle('#ff9b44')}>{totalSessions}</div>
              </div>
              <div style={kpiIconStyle('linear-gradient(135deg, #f093fb 0%, #f5576c 100%)')}>📊</div>
            </div>
          </Card>
        </Col>
        <Col span={6}>
          <Card size="small" style={cardStyle} bodyStyle={{ padding: '20px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={kpiLabelStyle}>Avg Auto-Detection</div>
                <div style={kpiValueStyle(avgAutoRate >= 70 ? '#55ce63' : avgAutoRate >= 40 ? '#faad14' : '#ff4d4f')}>
                  {avgAutoRate}%
                </div>
              </div>
              <div style={kpiIconStyle('linear-gradient(135deg, #fa709a 0%, #fee140 100%)')}>🤖</div>
            </div>
          </Card>
        </Col>
      </Row>

      {/* Charts Row */}
      {employees.length > 0 && (
        <Row gutter={16} style={{ marginBottom: '20px' }}>
          <Col span={16}>
            <Card size="small" style={cardStyle} bodyStyle={{ padding: '20px' }}>
              <div style={sectionTitleStyle}>Top Employees by Active Hours</div>
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={topEmployees} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#8c8c8c' }} />
                  <YAxis tick={{ fontSize: 11, fill: '#8c8c8c' }} />
                  <RechartsTooltip
                    contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
                    formatter={(value) => [`${value.toFixed(1)} hrs`, 'Hours']}
                  />
                  <Bar dataKey="hours" radius={[6, 6, 0, 0]} maxBarSize={40}>
                    {topEmployees.map((_, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Card>
          </Col>
          <Col span={8}>
            <Card size="small" style={cardStyle} bodyStyle={{ padding: '20px' }}>
              <div style={sectionTitleStyle}>Time by Source</div>
              {sourceData.length > 0 ? (
                <ResponsiveContainer width="100%" height={280}>
                  <PieChart>
                    <Pie
                      data={sourceData}
                      cx="50%"
                      cy="45%"
                      innerRadius={55}
                      outerRadius={90}
                      paddingAngle={3}
                      dataKey="value"
                      labelLine={false}
                      label={CustomPieLabel}
                    >
                      {sourceData.map((entry) => (
                        <Cell key={entry.key} fill={SOURCE_COLORS[entry.key] || '#8c8c8c'} />
                      ))}
                    </Pie>
                    <Legend
                      verticalAlign="bottom"
                      height={36}
                      formatter={(value) => <span style={{ fontSize: '12px', color: '#555' }}>{value}</span>}
                    />
                    <RechartsTooltip
                      contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
                      formatter={(value) => [`${value} hrs`, '']}
                    />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div style={{ height: 280, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#bfbfbf' }}>
                  No source data
                </div>
              )}
            </Card>
          </Col>
        </Row>
      )}

      {/* Employee Table */}
      <Card size="small" style={cardStyle} bodyStyle={{ padding: '0' }}>
        <div style={{ padding: '16px 20px 0', ...sectionTitleStyle }}>Employee Details</div>
        <Table
          columns={columns}
          dataSource={employees}
          rowKey="userId"
          size="small"
          loading={loading}
          pagination={{ pageSize: 50, style: { padding: '0 16px' } }}
          locale={{ emptyText: 'Select date range and click Generate Report to load data' }}
          style={{ borderRadius: '0 0 10px 10px' }}
          expandable={{
            expandedRowKeys: expandedRows,
            onExpandedRowsChange: setExpandedRows,
            expandedRowRender: (record) => (
              <div style={{ padding: '12px 20px', background: '#fafbfc' }}>
                <Row gutter={24}>
                  <Col span={14}>
                    <div style={{ fontSize: '13px', fontWeight: 600, marginBottom: '8px', color: '#1a1a2e' }}>
                      Task Breakdown
                    </div>
                    <Table columns={taskColumns} dataSource={record.taskBreakdown} rowKey="taskType" size="small" pagination={false} />
                  </Col>
                  <Col span={10}>
                    <div style={{ fontSize: '13px', fontWeight: 600, marginBottom: '8px', color: '#1a1a2e' }}>
                      Source Breakdown
                    </div>
                    <Table columns={sourceColumns} dataSource={record.sourceBreakdown} rowKey="source" size="small" pagination={false} />
                  </Col>
                </Row>
              </div>
            ),
          }}
        />
      </Card>
    </div>
  );
};

export default EmployeePerformanceReport;
